/**
 * Row builders for the per-booking export: every supplier line on a booking
 * (hotels, rounds, transfer, flights, miscellaneous) followed by a Total row
 * holding the booking's margins. Consumes the same AuditResult as the supplier
 * audit, so one load feeds both exports. Pure, so it can be unit tested.
 *
 * Costs follow updateBookingMargins (src/utils/methods.js): hotel, round, buggy
 * and transfer costs are in the supplier currency and divided by the booking's
 * exchange rate for GBP; flights and miscellaneous costs are already GBP.
 */

const CHECK_TOLERANCE = 0.05

/**
 * Column formats tell the workbook writer how to display each cell.
 *
 * @typedef {"text"|"date"|"time"|"number"|"money"|"rate"|"percent"} LineFormat
 * @typedef {{ key: string, header: string, width: number, format: LineFormat }} LineColumn
 */

/** @type {LineColumn[]} */
export const LINE_COLUMNS = [
	{ key: "reference", header: "Booking ref", width: 12, format: "text" },
	{ key: "supplier", header: "Supplier", width: 34, format: "text" },
	{ key: "type", header: "Type", width: 10, format: "text" },
	{ key: "leadName", header: "Lead name", width: 22, format: "text" },
	{ key: "travelDate", header: "Travel date", width: 12, format: "date" },
	{ key: "agent", header: "Agent", width: 18, format: "text" },
	{ key: "date", header: "Check-in / date", width: 14, format: "date" },
	{ key: "endDate", header: "Check-out / end", width: 14, format: "date" },
	{ key: "nights", header: "Nights", width: 8, format: "number" },
	{ key: "time", header: "Time", width: 8, format: "time" },
	{ key: "pax", header: "Players / pax", width: 12, format: "number" },
	{ key: "rooms", header: "Rooms", width: 26, format: "text" },
	{ key: "service", header: "Meal plan / buggies / service", width: 28, format: "text" },
	{ key: "reservationStatus", header: "Reservation status", width: 14, format: "text" },
	{ key: "supplierConfirmed", header: "Supplier confirmed", width: 12, format: "text" },
	{ key: "supplierReference", header: "Supplier reference", width: 20, format: "text" },
	{ key: "confirmedBy", header: "Confirmed by", width: 18, format: "text" },
	{ key: "confirmedOn", header: "Confirmed on", width: 12, format: "date" },
	{ key: "netCost", header: "Net cost", width: 12, format: "money" },
	{ key: "currency", header: "Currency", width: 9, format: "text" },
	{ key: "netCostGbp", header: "Net cost (GBP)", width: 14, format: "money" },
	{ key: "invoiceStatus", header: "Invoice status", width: 14, format: "text" },
	{ key: "invoiceAmount", header: "Invoice amount", width: 16, format: "text" },
	{ key: "invoicePaid", header: "Invoice paid", width: 14, format: "text" },
	{ key: "notes", header: "Notes", width: 40, format: "text" },
	{ key: "customerPrice", header: "Customer price", width: 14, format: "money" },
	{ key: "supplierCosts", header: "Supplier costs (GBP)", width: 16, format: "money" },
	{ key: "profit", header: "Profit", width: 12, format: "money" },
	{ key: "marginPercent", header: "Margin %", width: 10, format: "percent" },
	{ key: "commission", header: "Agent commission", width: 14, format: "money" },
	{ key: "paidByClient", header: "Paid by client", width: 14, format: "money" },
	{ key: "balance", header: "Balance outstanding", width: 16, format: "money" },
	{ key: "resortsPaid", header: "Resorts paid", width: 12, format: "text" },
	{ key: "exchangeRate", header: "Exchange rate", width: 12, format: "rate" },
	{ key: "check", header: "Lines vs margins", width: 22, format: "text" },
]

/** @type {LineColumn[]} */
export const SUMMARY_COLUMNS = [
	{ key: "reference", header: "Booking ref", width: 12, format: "text" },
	{ key: "leadName", header: "Lead name", width: 22, format: "text" },
	{ key: "leadEmail", header: "Client email", width: 30, format: "text" },
	{ key: "travelDate", header: "Travel date", width: 12, format: "date" },
	{ key: "agent", header: "Agent", width: 18, format: "text" },
	{ key: "pax", header: "Pax", width: 8, format: "number" },
	{ key: "hotels", header: "Hotels", width: 8, format: "number" },
	{ key: "rounds", header: "Rounds", width: 8, format: "number" },
	{ key: "supplierLines", header: "Supplier lines", width: 12, format: "number" },
	{ key: "unconfirmed", header: "Lines not confirmed", width: 16, format: "number" },
	{ key: "customerPrice", header: "Customer price", width: 14, format: "money" },
	{ key: "supplierCosts", header: "Supplier costs (GBP)", width: 16, format: "money" },
	{ key: "profit", header: "Profit", width: 12, format: "money" },
	{ key: "marginPercent", header: "Margin %", width: 10, format: "percent" },
	{ key: "commission", header: "Agent commission", width: 14, format: "money" },
	{ key: "paidByClient", header: "Paid by client", width: 14, format: "money" },
	{ key: "balance", header: "Balance outstanding", width: 16, format: "money" },
	{ key: "resortsPaid", header: "Resorts paid", width: 12, format: "text" },
	{ key: "check", header: "Lines vs margins", width: 22, format: "text" },
]

const yesNo = (value) => (value ? "Yes" : "No")

const isNumber = (value) => typeof value === "number" && Number.isFinite(value)

const round2 = (value) => Math.round(value * 100) / 100

const toGbp = (amount, rate) => (isNumber(amount) ? round2(amount / rate) : null)

const dateOnly = (millis) => {
	if (!isNumber(millis)) return null
	const date = new Date(millis)
	return new Date(date.getFullYear(), date.getMonth(), date.getDate())
}

const pad = (value) => String(value).padStart(2, "0")

const timeOfDay = (millis) => {
	if (!isNumber(millis)) return ""
	const date = new Date(millis)
	return `${pad(date.getHours())}:${pad(date.getMinutes())}`
}

const formatMoney = (value) => value.toFixed(2)

const joinNotes = (parts) => parts.filter(Boolean).join(" | ")

const invoiceStatus = (invoices) => invoices.map((invoice) => invoice.status || "?").join("; ")

/**
 * Each invoice's amount in its own currency, in the same order as the
 * statuses. Invoices hold one amount, so for a PARTIAL invoice this is the
 * invoice total, not what has been paid so far.
 */
const invoiceAmount = (invoices) =>
	invoices
		.map((invoice) => {
			if (!isNumber(invoice.amount)) return "?"
			const amount = invoice.amount.toLocaleString("en-GB", { minimumFractionDigits: 2, maximumFractionDigits: 2 })
			return [amount, invoice.currency].filter(Boolean).join(" ")
		})
		.join("; ")

const invoicePaid = (invoices) =>
	invoices
		.map((invoice) => {
			if (!isNumber(invoice.paidMillis)) return ""
			const date = new Date(invoice.paidMillis)
			return `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`
		})
		.filter(Boolean)
		.join("; ")

/**
 * Golfers rounded up to an even number, as the round component does when it
 * adds up buggy costs (one buggy per two players).
 */
const evenPlayers = (players) => (players % 2 === 0 ? players : players + 1)

/**
 * The booking-level columns every row carries, so filtering on a booking ref
 * or a lead name keeps the whole group.
 */
const bookingColumns = (booking) => ({
	reference: booking.reference,
	leadName: booking.leadName,
	travelDate: dateOnly(booking.travelMillis),
	agent: booking.agentName,
})

const confirmationColumns = (confirmation) => ({
	supplierReference: confirmation.reference,
	confirmedBy: confirmation.contact,
	confirmedOn: dateOnly(confirmation.receivedMillis),
})

const hotelLine = (booking, hotel, rate) => ({
	...bookingColumns(booking),
	supplier: hotel.supplier.name,
	type: "Hotel",
	date: dateOnly(hotel.checkInMillis),
	endDate: dateOnly(hotel.checkOutMillis),
	nights: hotel.nights,
	pax: booking.pax,
	rooms: hotel.rooms,
	service: hotel.boardType,
	reservationStatus: hotel.reservationStatus,
	supplierConfirmed: yesNo(hotel.supplierConfirmed),
	...confirmationColumns(hotel.confirmation),
	netCost: hotel.cost,
	currency: hotel.currency,
	netCostGbp: toGbp(hotel.cost, rate),
	invoiceStatus: invoiceStatus(hotel.invoices),
	invoiceAmount: invoiceAmount(hotel.invoices),
	invoicePaid: invoicePaid(hotel.invoices),
	notes: joinNotes([hotel.comments, hotel.mealTimes && `Meal times: ${hotel.mealTimes}`]),
})

/**
 * Green fees are cost per golfer x players; buggies are the stored per-golfer
 * buggy cost x players rounded up to even, matching margins.buggies_cost.
 */
const roundCost = (round) => {
	const greenFees = isNumber(round.costPerGolfer) && round.players > 0 ? round.costPerGolfer * round.players : null
	const buggyPlayers = evenPlayers(round.players)
	const buggies = isNumber(round.buggiesUnitCost) && round.buggiesUnitCost > 0 && round.players > 0 ? round.buggiesUnitCost * buggyPlayers : null

	if (greenFees === null && buggies === null) return { cost: null, breakdown: "" }

	const parts = []
	if (greenFees !== null) parts.push(`Green fees ${round.players} x ${formatMoney(round.costPerGolfer)}`)
	if (buggies !== null) parts.push(`buggies ${buggyPlayers} x ${formatMoney(round.buggiesUnitCost)}`)

	return { cost: round2((greenFees || 0) + (buggies || 0)), breakdown: parts.join(", ") }
}

const roundLine = (booking, round, rate) => {
	const { cost, breakdown } = roundCost(round)

	return {
		...bookingColumns(booking),
		supplier: round.supplier.name,
		type: "Golf",
		date: dateOnly(round.teeTimeMillis),
		time: timeOfDay(round.teeTimeMillis),
		pax: round.players,
		service: round.buggies,
		reservationStatus: round.reservationStatus,
		supplierConfirmed: yesNo(round.supplierConfirmed),
		...confirmationColumns(round.confirmation),
		netCost: cost,
		currency: round.currency,
		netCostGbp: toGbp(cost, rate),
		invoiceStatus: invoiceStatus(round.invoices),
		invoiceAmount: invoiceAmount(round.invoices),
		invoicePaid: invoicePaid(round.invoices),
		notes: joinNotes([breakdown, round.comments]),
	}
}

const hasTransfer = (transport) => {
	const type = transport.transferTypeKey
	return Boolean(transport.supplier) || (isNumber(transport.cost) && transport.cost > 0) || (type !== "" && type !== "nothing")
}

const transferLine = (booking, transport, rate) => ({
	...bookingColumns(booking),
	supplier: transport.supplier?.name || transport.lookupProvider || "(No transfer supplier set)",
	type: "Transfer",
	date: dateOnly(booking.travelMillis),
	time: transport.lookupPickupTime,
	pax: booking.pax,
	service: transport.transferType,
	reservationStatus: transport.reservationStatus,
	supplierConfirmed: yesNo(transport.supplierConfirmed),
	...confirmationColumns(transport.confirmation),
	supplierReference: transport.confirmation.reference || transport.transfersReference,
	netCost: transport.cost,
	currency: transport.supplier?.currency || "",
	netCostGbp: toGbp(transport.cost, rate),
	invoiceStatus: invoiceStatus(transport.invoices),
	invoiceAmount: invoiceAmount(transport.invoices),
	invoicePaid: invoicePaid(transport.invoices),
	notes: joinNotes([transport.lookupDescription, transport.notes]),
})

const flightService = (flights) => {
	const route = flights.originAirport && flights.arrivalAirport ? `${flights.originAirport} → ${flights.arrivalAirport}` : flights.arrivalAirport || flights.lookupDestination
	const numbers = [flights.outboundFlightNumber, flights.returnFlightNumber].filter(Boolean).join(" / ")
	return [route, numbers].filter(Boolean).join(", ")
}

const flightLine = (booking, flights) => ({
	...bookingColumns(booking),
	supplier: flights.provider || "Flights",
	type: "Flight",
	date: dateOnly(flights.departureMillis),
	endDate: dateOnly(flights.returnDepartureMillis),
	time: timeOfDay(flights.arrivalMillis) || flights.lookupArrivalTime,
	pax: booking.pax,
	service: flightService(flights),
	supplierConfirmed: yesNo(flights.booked),
	...confirmationColumns(flights.confirmation),
	supplierReference: flights.reference || flights.confirmation.reference,
	netCost: flights.cost,
	currency: "GBP",
	netCostGbp: flights.cost,
	notes: flights.arrivalMillis ? "Time is the outbound arrival" : "",
})

const miscLine = (booking, amount) => ({
	...bookingColumns(booking),
	supplier: "Miscellaneous costs",
	type: "Misc",
	netCost: amount,
	currency: "GBP",
	netCostGbp: amount,
})

/**
 * "OK" when the itemised GBP total matches the stored supplier invoices total
 * within a few pence, otherwise what the difference is.
 */
const checkLabel = (linesTotal, supplierInvoices) => {
	if (!isNumber(supplierInvoices)) return "No margins saved"
	const difference = round2(linesTotal - supplierInvoices)
	if (Math.abs(difference) <= CHECK_TOLERANCE) return "OK"
	return `Lines ${difference > 0 ? "over" : "under"} by £${formatMoney(Math.abs(difference))}`
}

const marginFigures = (booking, linesTotal) => {
	const { margins } = booking
	const marginPercent = isNumber(margins.profit) && isNumber(margins.customerPrice) && margins.customerPrice !== 0 ? margins.profit / margins.customerPrice : null

	return {
		customerPrice: margins.customerPrice,
		supplierCosts: margins.supplierInvoices,
		profit: margins.profit,
		marginPercent,
		commission: margins.agentsMargin,
		paidByClient: booking.paidByClient,
		balance: booking.balanceOutstanding,
		resortsPaid: yesNo(booking.resortsPaid),
		exchangeRate: margins.exchangeRate,
		check: checkLabel(linesTotal, margins.supplierInvoices),
	}
}

/**
 * Every supplier line on one booking, in the order hotels, rounds, transfer,
 * flights, miscellaneous.
 *
 * @param {import("./types").AuditBooking} booking
 */
export const buildBookingLines = (booking) => {
	const rate = booking.margins.exchangeRate
	const lines = [...booking.hotels.map((hotel) => hotelLine(booking, hotel, rate)), ...booking.rounds.map((round) => roundLine(booking, round, rate))]

	if (hasTransfer(booking.transport)) lines.push(transferLine(booking, booking.transport, rate))
	if (booking.flights.required || booking.flights.booked) lines.push(flightLine(booking, booking.flights))

	const misc = booking.margins.miscellaneousCosts
	if (isNumber(misc) && misc > 0) lines.push(miscLine(booking, misc))

	return lines
}

const sumGbp = (lines) => round2(lines.reduce((total, line) => total + (isNumber(line.netCostGbp) ? line.netCostGbp : 0), 0))

/**
 * One group per booking: its supplier lines and a Total row.
 *
 * @param {import("./types").AuditResult} result
 * @returns {Array<{ booking: import("./types").AuditBooking, lines: Object[], total: Object }>}
 */
export const buildBookingGroups = (result) =>
	result.bookings.map((booking) => {
		const lines = buildBookingLines(booking)
		const linesTotal = sumGbp(lines)

		return {
			booking,
			lines,
			total: {
				...bookingColumns(booking),
				supplier: "Booking total",
				type: "Total",
				netCostGbp: linesTotal,
				...marginFigures(booking, linesTotal),
			},
		}
	})

/**
 * One row per booking for the Bookings tab, from the groups so both tabs agree.
 *
 * @param {ReturnType<typeof buildBookingGroups>} groups
 */
export const buildBookingSummaryRows = (groups) =>
	groups.map(({ booking, lines, total }) => {
		const supplierLines = lines.filter((line) => line.type !== "Misc")

		return {
			...bookingColumns(booking),
			leadEmail: booking.leadEmail,
			pax: booking.pax,
			hotels: booking.hotels.length,
			rounds: booking.rounds.length,
			supplierLines: supplierLines.length,
			unconfirmed: supplierLines.filter((line) => line.supplierConfirmed === "No").length,
			customerPrice: total.customerPrice,
			supplierCosts: total.supplierCosts,
			profit: total.profit,
			marginPercent: total.marginPercent,
			commission: total.commission,
			paidByClient: total.paidByClient,
			balance: total.balance,
			resortsPaid: total.resortsPaid,
			check: total.check,
		}
	})
